const MIN_FONT_SIZE_PX = 8;
const DEFAULT_MAX_LINES = 4;
const DEFAULT_MIN_LINES = 1;
const DEFAULT_LINE_HEIGHT_MULT = 1.2;
const DEFAULT_VERTICAL_LINE_HEIGHT_MULT = 1;

let measureCtx = null;

const getMeasureCtx = () => {
  if (measureCtx) return measureCtx;
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  measureCtx = canvas.getContext("2d");
  return measureCtx;
};

const setCanvasFont = (ctx, fontSize, fontFamily) => {
  ctx.font = `${fontSize}px "${fontFamily}"`;
};

const measureLine = (ctx, text, fontSize, fontFamily) => {
  setCanvasFont(ctx, fontSize, fontFamily);
  const metrics = ctx.measureText(text || "");
  const ascent = metrics.actualBoundingBoxAscent ?? fontSize * 0.78;
  const descent = metrics.actualBoundingBoxDescent ?? fontSize * 0.22;
  return {
    width: metrics.width,
    ascent,
    descent,
    height: ascent + descent,
  };
};

const wrapTextToWidth = ({ ctx, text, fontSize, fontFamily, maxWidth }) => {
  const paragraphs = String(text || "").split(/\n/);
  const lines = [];

  paragraphs.forEach((paragraph) => {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push("");
      return;
    }

    let current = words[0];
    for (let index = 1; index < words.length; index += 1) {
      const trial = `${current} ${words[index]}`;
      const { width } = measureLine(ctx, trial, fontSize, fontFamily);
      if (width <= maxWidth) {
        current = trial;
      } else {
        lines.push(current);
        current = words[index];
      }
    }
    lines.push(current);
  });

  return lines.length > 0 ? lines : [""];
};

const wrapTextVertical = ({ text }) => {
  const chars = Array.from(String(text || "")).filter(
    (char) => !/\s/.test(char)
  );
  return chars.length > 0 ? chars : [""];
};

const wrapLines = ({
  ctx,
  text,
  fontSize,
  fontFamily,
  maxWidth,
  orientation,
}) => {
  if (orientation === "vertical") {
    return wrapTextVertical({ text });
  }
  return wrapTextToWidth({ ctx, text, fontSize, fontFamily, maxWidth });
};

const measureBlock = ({
  ctx,
  lines,
  fontSize,
  fontFamily,
  lineHeightMult,
}) => {
  const lineMetrics = lines.map((line) =>
    measureLine(ctx, line, fontSize, fontFamily)
  );
  const maxWidth = lineMetrics.reduce(
    (max, line) => Math.max(max, line.width),
    0
  );
  const firstAscent = lineMetrics[0]?.ascent || fontSize * 0.78;
  const lastDescent =
    lineMetrics[lineMetrics.length - 1]?.descent || fontSize * 0.22;
  const lineAdvance = fontSize * lineHeightMult;
  const totalHeight =
    lines.length <= 1
      ? firstAscent + lastDescent
      : (lines.length - 1) * lineAdvance + firstAscent + lastDescent;

  return {
    maxWidth,
    totalHeight,
    firstAscent,
    lastDescent,
    lineAdvance,
  };
};

const estimateMaxFontSize = ({
  boxWidth,
  boxHeight,
  minLines,
  lineHeightMult,
}) => {
  const lineCount = Math.max(1, minLines);
  const heightFactor = lineCount <= 1 ? 1 : (lineCount - 1) * lineHeightMult + 1;
  const byHeight = boxHeight / heightFactor;
  return Math.max(MIN_FONT_SIZE_PX, Math.ceil(Math.min(byHeight, boxWidth)));
};

const layoutFits = ({
  metrics,
  boxWidth,
  boxHeight,
  lineCount,
  maxLines,
  orientation,
}) => {
  if (metrics.maxWidth > boxWidth || metrics.totalHeight > boxHeight) {
    return false;
  }
  if (orientation === "vertical") return true;
  return lineCount <= maxLines;
};

export const fitTextInBox = ({
  ctx: ctxArg,
  text,
  boxWidth,
  boxHeight,
  fontFamily,
  minimumLines,
  maximumLines,
  minimumFontSize,
  requestedFontSize,
  lineHeightMult,
  orientation: orientationArg,
}) => {
  const ctx = ctxArg || getMeasureCtx();
  if (!ctx) {
    return { ok: false, reason: "noCanvas" };
  }
  const orientation =
    String(orientationArg || "horizontal") === "vertical"
      ? "vertical"
      : "horizontal";
  const minLines = Math.max(
    DEFAULT_MIN_LINES,
    Number(minimumLines) || DEFAULT_MIN_LINES
  );
  const maxLines = Math.max(
    minLines,
    Number(maximumLines) || DEFAULT_MAX_LINES
  );
  const minSize = Math.max(
    MIN_FONT_SIZE_PX,
    Number(minimumFontSize) || MIN_FONT_SIZE_PX
  );
  const lineHeight =
    Number(lineHeightMult) > 0
      ? Number(lineHeightMult)
      : orientation === "vertical"
        ? DEFAULT_VERTICAL_LINE_HEIGHT_MULT
        : DEFAULT_LINE_HEIGHT_MULT;

  if (!String(text || "").trim() || boxWidth <= 0 || boxHeight <= 0) {
    return { ok: false, reason: "emptyTextOrBox" };
  }

  const requested = Number(requestedFontSize);
  if (Number.isFinite(requested) && requested > 0) {
    const fontSize = Math.max(1, Math.round(requested));
    const lines = wrapLines({
      ctx,
      text,
      fontSize,
      fontFamily,
      maxWidth: boxWidth,
      orientation,
    });
    const metrics = measureBlock({
      ctx,
      lines,
      fontSize,
      fontFamily,
      lineHeightMult: lineHeight,
    });
    return { ok: true, fontSize, lines, metrics, lineHeightMult: lineHeight };
  }

  const verticalLines =
    orientation === "vertical" ? wrapTextVertical({ text }) : null;
  let low = minSize;
  let high = estimateMaxFontSize({
    boxWidth,
    boxHeight,
    minLines: verticalLines ? verticalLines.length : minLines,
    lineHeightMult: lineHeight,
  });
  if (high < minSize) {
    high = minSize;
  }
  let best = null;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const lines = wrapLines({
      ctx,
      text,
      fontSize: mid,
      fontFamily,
      maxWidth: boxWidth,
      orientation,
    });
    const metrics = measureBlock({
      ctx,
      lines,
      fontSize: mid,
      fontFamily,
      lineHeightMult: lineHeight,
    });

    if (
      layoutFits({
        metrics,
        boxWidth,
        boxHeight,
        lineCount: lines.length,
        maxLines,
        orientation,
      })
    ) {
      best = { fontSize: mid, lines, metrics, lineHeightMult: lineHeight };
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  if (!best) {
    return { ok: false, reason: "textOverflow" };
  }

  return { ok: true, ...best };
};
